import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import api from '../../services/api';
import { getMediaUrl } from '../../services/api';
import './EmployeeListSection.css';

const EmployeeListSection = ({ section, layout = 'default' }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await api.get('/employees/', { params: { page_size: 12 } });
      const data = response.data.results || response.data;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="employee-section homepage-section">
        <Container>
          <h2 className="section-title">{section.title}</h2>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  if (employees.length === 0) return null;

  const renderGridLayout = () => (
    <Row className="g-4">
      {employees.map(employee => (
        <Col key={employee.id} lg={4} md={6} sm={12}>
          <Card className="employee-card h-100 shadow-sm">
            {employee.photo && (
              <div className="employee-image-wrapper">
                <Card.Img 
                  variant="top" 
                  src={getMediaUrl(employee.photo)} 
                  alt={employee.name}
                  className="employee-image"
                />
              </div>
            )}
            <Card.Body>
              <Card.Title className="employee-name">
                {employee.name}
              </Card.Title>
              <p className="employee-position text-success fw-bold mb-2">
                {employee.position}
              </p>
              {employee.department && (
                <p className="text-muted small mb-2">
                  <i className="fas fa-building me-2"></i>
                  {employee.department.name || employee.department}
                </p>
              )}
              {employee.qualification && (
                <p className="text-muted small mb-2">
                  <i className="fas fa-graduation-cap me-2"></i>
                  {employee.qualification}
                </p>
              )}
              {employee.email && (
                <p className="contact-info mb-1">
                  <i className="fas fa-envelope me-2 text-info"></i>
                  <a href={`mailto:${employee.email}`}>{employee.email}</a>
                </p>
              )}
              {employee.phone && (
                <p className="contact-info mb-0">
                  <i className="fas fa-phone me-2 text-success"></i>
                  <a href={`tel:${employee.phone}`}>{employee.phone}</a>
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderCompactLayout = () => (
    <Row className="g-3">
      {employees.map(employee => (
        <Col key={employee.id} lg={3} md={4} sm={6} xs={12}>
          <div className="employee-compact-card text-center p-3 rounded border">
            {employee.photo && (
              <div className="employee-avatar mb-3">
                <img 
                  src={getMediaUrl(employee.photo)} 
                  alt={employee.name}
                  className="rounded-circle"
                />
              </div>
            )}
            <h5 className="employee-name-compact mb-1">{employee.name}</h5>
            <p className="text-success small fw-bold mb-1">{employee.position}</p>
            <p className="text-muted small mb-2">
              {employee.department && (employee.department.name || employee.department)}
            </p>
            {employee.phone && (
              <p className="text-muted small">
                <i className="fas fa-phone me-1"></i>{employee.phone}
              </p>
            )}
            {employee.email && (
              <p className="text-muted small">
                <a href={`mailto:${employee.email}`} className="text-decoration-none">
                  <i className="fas fa-envelope me-1"></i>Email
                </a>
              </p>
            )}
          </div>
        </Col>
      ))}
    </Row>
  );

  const renderTableLayout = () => (
    <div className="employee-table-wrapper">
      <table className="employee-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Position</th>
            <th>Department</th>
            <th>Qualification</th>
            <th>Contact</th>
          </tr>
        </thead>
        <tbody>
          {employees.map(employee => (
            <tr key={employee.id} className="employee-row">
              <td className="employee-cell-name">
                {employee.photo && (
                  <img 
                    src={getMediaUrl(employee.photo)} 
                    alt={employee.name}
                    className="employee-table-image me-2"
                  />
                )}
                <strong>{employee.name}</strong>
              </td>
              <td className="employee-cell-position">{employee.position}</td>
              <td className="employee-cell-dept">
                {employee.department_name  || '-'}
              </td>
              <td className="employee-cell-qual">
                {employee.qualification && (
                  <><i className="fas fa-graduation-cap me-1 text-info"></i>{employee.qualification}</>
                )}
              </td>
              <td className="employee-cell-contact">
                {employee.phone && (
                  <a href={`tel:${employee.phone}`} className="btn-icon me-2" title="Call">
                    <i className="fas fa-phone"></i>
                  </a>
                )}
                {employee.email && (
                  <a href={`mailto:${employee.email}`} className="btn-icon" title="Email">
                    <i className="fas fa-envelope"></i>
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="employee-section homepage-section">
      <Container>
        <h2 className="section-title">{section.title}</h2>
        {layout === 'compact' && renderCompactLayout()}
        {layout === 'table' && renderTableLayout()}
        {layout === 'default' && renderGridLayout()}
      </Container>
    </section>
  );
};

export default EmployeeListSection;
